/**
 * Contract ABIs for Takeover
 *
 * Derived from takeover-contracts/src:
 *   - TakeoverFeeSplitManager.sol (main contract)
 *   - bases/SeatLifecycle.sol     (seats mapping, getHolderSeats, getHolderPools, holderSeatCount)
 *   - bases/SeatAccounting.sol    (pendingFees overloads, amountClaimed, protocolFeesEth)
 *   - bases/SeatTaxation.sol      (currentBalance, timeUntilForfeiture, cooldownRemaining, taxRateBps)
 */

export const TAKEOVER_MANAGER_ABI = [
  // =========================================================================
  // View Functions - Pool Level
  // =========================================================================
  {
    name: "managerFees",
    type: "function",
    stateMutability: "view",
    inputs: [],
    outputs: [{ name: "", type: "uint256" }],
  },
  {
    name: "splitFees",
    type: "function",
    stateMutability: "view",
    inputs: [],
    outputs: [{ name: "", type: "uint256" }],
  },
  {
    name: "taxRateBps",
    type: "function",
    stateMutability: "view",
    inputs: [],
    outputs: [{ name: "", type: "uint256" }],
  },
  {
    name: "poolRegistered",
    type: "function",
    stateMutability: "view",
    inputs: [{ name: "_poolId", type: "bytes32" }],
    outputs: [{ name: "", type: "bool" }],
  },
  {
    name: "poolCreator",
    type: "function",
    stateMutability: "view",
    inputs: [{ name: "_poolId", type: "bytes32" }],
    outputs: [{ name: "", type: "address" }],
  },
  {
    name: "poolFlaunch",
    type: "function",
    stateMutability: "view",
    inputs: [{ name: "_poolId", type: "bytes32" }],
    outputs: [{ name: "", type: "address" }],
  },
  {
    name: "protocolFeesUsdc",
    type: "function",
    stateMutability: "view",
    inputs: [],
    outputs: [{ name: "", type: "uint256" }],
  },
  {
    name: "protocolFeeBps",
    type: "function",
    stateMutability: "view",
    inputs: [],
    outputs: [{ name: "", type: "uint256" }],
  },
  {
    name: "protocolFeesEth",
    type: "function",
    stateMutability: "view",
    inputs: [],
    outputs: [{ name: "", type: "uint256" }],
  },
  {
    name: "taxRevenueUsdc",
    type: "function",
    stateMutability: "view",
    inputs: [],
    outputs: [{ name: "", type: "uint256" }],
  },
  // =========================================================================
  // View Functions - Seat Level
  // =========================================================================
  // Auto-generated getter from: mapping(PoolId => mapping(uint256 => SeatInfo)) public seats
  // SeatInfo { address holder; uint128 price; uint128 deposit; uint64 lastTaxTime; uint64 lastPriceChangeTime }
  {
    name: "seats",
    type: "function",
    stateMutability: "view",
    inputs: [
      { name: "_poolId", type: "bytes32" },
      { name: "_seatId", type: "uint256" },
    ],
    outputs: [
      { name: "holder", type: "address" },
      { name: "price", type: "uint128" },
      { name: "deposit", type: "uint128" },
      { name: "lastTaxTime", type: "uint64" },
      { name: "lastPriceChangeTime", type: "uint64" },
    ],
  },
  {
    name: "currentBalance",
    type: "function",
    stateMutability: "view",
    inputs: [
      { name: "_poolId", type: "bytes32" },
      { name: "_seatId", type: "uint256" },
    ],
    outputs: [{ name: "", type: "int256" }],
  },
  // pendingFees(poolId, seatId) - per seat
  {
    name: "pendingFees",
    type: "function",
    stateMutability: "view",
    inputs: [
      { name: "_poolId", type: "bytes32" },
      { name: "_seatId", type: "uint256" },
    ],
    outputs: [{ name: "", type: "uint256" }],
  },
  // pendingFees(poolId, holder) - per holder per pool
  {
    name: "pendingFees",
    type: "function",
    stateMutability: "view",
    inputs: [
      { name: "_poolId", type: "bytes32" },
      { name: "_holder", type: "address" },
    ],
    outputs: [{ name: "", type: "uint256" }],
  },
  // pendingFees(holder) - all pools for holder
  {
    name: "pendingFees",
    type: "function",
    stateMutability: "view",
    inputs: [{ name: "_holder", type: "address" }],
    outputs: [{ name: "", type: "uint256" }],
  },
  {
    name: "timeUntilForfeiture",
    type: "function",
    stateMutability: "view",
    inputs: [
      { name: "_poolId", type: "bytes32" },
      { name: "_seatId", type: "uint256" },
    ],
    outputs: [{ name: "", type: "uint256" }],
  },
  {
    name: "cooldownRemaining",
    type: "function",
    stateMutability: "view",
    inputs: [
      { name: "_poolId", type: "bytes32" },
      { name: "_seatId", type: "uint256" },
    ],
    outputs: [{ name: "", type: "uint256" }],
  },
  {
    name: "amountClaimed",
    type: "function",
    stateMutability: "view",
    inputs: [
      { name: "_poolId", type: "bytes32" },
      { name: "_seatId", type: "uint256" },
    ],
    outputs: [{ name: "", type: "uint256" }],
  },
  // =========================================================================
  // View Functions - Holder Queries
  // =========================================================================
  {
    name: "getHolderPools",
    type: "function",
    stateMutability: "view",
    inputs: [{ name: "_holder", type: "address" }],
    outputs: [{ name: "", type: "bytes32[]" }],
  },
  {
    name: "getHolderSeats",
    type: "function",
    stateMutability: "view",
    inputs: [
      { name: "_poolId", type: "bytes32" },
      { name: "_holder", type: "address" },
    ],
    outputs: [{ name: "", type: "uint256[]" }],
  },
  {
    name: "holderSeatCount",
    type: "function",
    stateMutability: "view",
    inputs: [
      { name: "_holder", type: "address" },
      { name: "_poolId", type: "bytes32" },
    ],
    outputs: [{ name: "", type: "uint256" }],
  },
  // =========================================================================
  // View Functions - Constants
  // =========================================================================
  {
    name: "TOTAL_SEATS",
    type: "function",
    stateMutability: "view",
    inputs: [],
    outputs: [{ name: "", type: "uint256" }],
  },
  {
    name: "SEAT_SHARE",
    type: "function",
    stateMutability: "view",
    inputs: [],
    outputs: [{ name: "", type: "uint256" }],
  },
  {
    name: "PRICE_COOLDOWN",
    type: "function",
    stateMutability: "view",
    inputs: [],
    outputs: [{ name: "", type: "uint256" }],
  },
  {
    name: "DEFAULT_TAX_RATE_BPS",
    type: "function",
    stateMutability: "view",
    inputs: [],
    outputs: [{ name: "", type: "uint256" }],
  },
  {
    name: "MIN_TAX_RATE",
    type: "function",
    stateMutability: "view",
    inputs: [],
    outputs: [{ name: "", type: "uint256" }],
  },
  {
    name: "MAX_TAX_RATE",
    type: "function",
    stateMutability: "view",
    inputs: [],
    outputs: [{ name: "", type: "uint256" }],
  },
  {
    name: "ONE_WEEK",
    type: "function",
    stateMutability: "view",
    inputs: [],
    outputs: [{ name: "", type: "uint256" }],
  },
  // =========================================================================
  // View Functions - Pool Extras
  // =========================================================================
  {
    name: "poolTokenId",
    type: "function",
    stateMutability: "view",
    inputs: [{ name: "_poolId", type: "bytes32" }],
    outputs: [{ name: "", type: "uint256" }],
  },
  {
    name: "approvedForDeposit",
    type: "function",
    stateMutability: "view",
    inputs: [
      { name: "_flaunch", type: "address" },
      { name: "_tokenId", type: "uint256" },
    ],
    outputs: [{ name: "", type: "bool" }],
  },
  {
    name: "permissionlessDeposits",
    type: "function",
    stateMutability: "view",
    inputs: [],
    outputs: [{ name: "", type: "bool" }],
  },
  {
    name: "approvedZaps",
    type: "function",
    stateMutability: "view",
    inputs: [{ name: "_zap", type: "address" }],
    outputs: [{ name: "", type: "bool" }],
  },
  // =========================================================================
  // View Functions - Admin State
  // =========================================================================
  {
    name: "USDC",
    type: "function",
    stateMutability: "view",
    inputs: [],
    outputs: [{ name: "", type: "address" }],
  },
  {
    name: "managerOwner",
    type: "function",
    stateMutability: "view",
    inputs: [],
    outputs: [{ name: "", type: "address" }],
  },
  {
    name: "owner",
    type: "function",
    stateMutability: "view",
    inputs: [],
    outputs: [{ name: "", type: "address" }],
  },
  {
    name: "protocolFeeReceiver",
    type: "function",
    stateMutability: "view",
    inputs: [],
    outputs: [{ name: "", type: "address" }],
  },
  {
    name: "protocolFeeReceiverEth",
    type: "function",
    stateMutability: "view",
    inputs: [],
    outputs: [{ name: "", type: "address" }],
  },
  {
    name: "buybackContract",
    type: "function",
    stateMutability: "view",
    inputs: [],
    outputs: [{ name: "", type: "address" }],
  },
  // =========================================================================
  // Write Functions - Seat Management
  // =========================================================================
  // Unified batch: claim empty seats + buy occupied seats in one tx
  {
    name: "addBatch",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [
      { name: "_poolId", type: "bytes32" },
      {
        name: "_params",
        type: "tuple[]",
        components: [
          { name: "seatId", type: "uint256" },
          { name: "price", type: "uint256" },
          { name: "maxPrice", type: "uint256" },
          { name: "amount", type: "uint256" },
        ],
      },
    ],
    outputs: [],
  },
  {
    name: "setPrice",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [
      { name: "_poolId", type: "bytes32" },
      { name: "_seatId", type: "uint256" },
      { name: "_newPrice", type: "uint256" },
    ],
    outputs: [],
  },
  {
    name: "addDeposit",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [
      { name: "_poolId", type: "bytes32" },
      { name: "_seatId", type: "uint256" },
      { name: "_amount", type: "uint256" },
    ],
    outputs: [],
  },
  {
    name: "withdrawDeposit",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [
      { name: "_poolId", type: "bytes32" },
      { name: "_seatId", type: "uint256" },
      { name: "_amount", type: "uint256" },
    ],
    outputs: [],
  },
  {
    name: "abandonSeat",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [
      { name: "_poolId", type: "bytes32" },
      { name: "_seatId", type: "uint256" },
    ],
    outputs: [],
  },
  // =========================================================================
  // Write Functions - Fee Claiming (ETH fees from seat holding)
  // =========================================================================
  // Claim ETH fees for specified pools (use getHolderPools to get all pool IDs)
  {
    name: "claimFees",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [{ name: "_poolIds", type: "bytes32[]" }],
    outputs: [{ name: "total", type: "uint256" }],
  },
  // Claim ETH fees from empty seats for a pool (callable by pool creator)
  {
    name: "claimCreatorFees",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [{ name: "_poolId", type: "bytes32" }],
    outputs: [{ name: "total", type: "uint256" }],
  },
  // Withdraw the deposited Flaunch NFT if creator controls all 100 seats
  {
    name: "creatorWithdrawCoin",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [{ name: "_poolId", type: "bytes32" }],
    outputs: [],
  },
  // =========================================================================
  // FeeSplitManager override (returns 0, seat holders use claimFees)
  // =========================================================================
  {
    name: "claim",
    type: "function",
    stateMutability: "pure",
    inputs: [],
    outputs: [{ name: "", type: "uint256" }],
  },
  // =========================================================================
  // Write Functions - Protocol Fee Withdrawal (owner-only)
  // =========================================================================
  {
    name: "withdrawProtocolFees",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [],
    outputs: [],
  },
  {
    name: "withdrawTaxRevenueForPools",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [{ name: "_poolIds", type: "bytes32[]" }],
    outputs: [],
  },
  {
    name: "withdrawTaxRevenue",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [],
    outputs: [],
  },
  // =========================================================================
  // Write Functions - Graduation (GraduationManager)
  // =========================================================================
  {
    name: "graduate",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [{ name: "_poolId", type: "bytes32" }],
    outputs: [],
  },
  {
    name: "graduationReady",
    type: "function",
    stateMutability: "view",
    inputs: [{ name: "_poolId", type: "bytes32" }],
    outputs: [{ name: "", type: "bool" }],
  },
  {
    name: "graduated",
    type: "function",
    stateMutability: "view",
    inputs: [{ name: "_poolId", type: "bytes32" }],
    outputs: [{ name: "", type: "bool" }],
  },
  // =========================================================================
  // Write Functions - Permissionless Tax Poke
  // =========================================================================
  {
    name: "pokeTax",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [
      { name: "_poolIds", type: "bytes32[]" },
      { name: "_seatIds", type: "uint256[][]" },
    ],
    outputs: [],
  },
  // =========================================================================
  // Write Functions - Admin Configuration (onlyManagerOwner)
  // =========================================================================
  {
    name: "setTaxRate",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [{ name: "_newRate", type: "uint256" }],
    outputs: [],
  },
  {
    name: "setProtocolFee",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [{ name: "_newFee", type: "uint256" }],
    outputs: [],
  },
  {
    name: "setProtocolFeeReceiver",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [{ name: "_receiver", type: "address" }],
    outputs: [],
  },
  {
    name: "setProtocolFeeReceiverEth",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [{ name: "_receiver", type: "address" }],
    outputs: [],
  },
  {
    name: "setBuybackContract",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [{ name: "_buyback", type: "address" }],
    outputs: [],
  },
  {
    name: "setApprovedZap",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [
      { name: "_zap", type: "address" },
      { name: "_approved", type: "bool" },
    ],
    outputs: [],
  },
  {
    name: "approveForDeposit",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [
      { name: "_flaunch", type: "address" },
      { name: "_tokenId", type: "uint256" },
      { name: "_approved", type: "bool" },
    ],
    outputs: [],
  },
  {
    name: "setPermissionlessDeposits",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [{ name: "_open", type: "bool" }],
    outputs: [],
  },
  {
    name: "withdrawProtocolFeesEth",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [],
    outputs: [],
  },
  {
    name: "sweepExcessEth",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [],
    outputs: [],
  },
  {
    name: "transferManagerOwnership",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [{ name: "_newManagerOwner", type: "address" }],
    outputs: [],
  },
  // =========================================================================
  // Write Functions - Deposit (existing NFT)
  // =========================================================================
  {
    name: "deposit",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [
      {
        name: "_flaunchToken",
        type: "tuple",
        components: [
          { name: "flaunch", type: "address" },
          { name: "tokenId", type: "uint256" },
        ],
      },
      { name: "_creator", type: "address" },
      { name: "_data", type: "bytes" },
    ],
    outputs: [],
  },
  // =========================================================================
  // Custom Errors (from ITakeoverManager + SeatAccounting)
  // =========================================================================
  { type: "error", name: "SeatAlreadyClaimed", inputs: [] },
  { type: "error", name: "SeatNotClaimed", inputs: [] },
  { type: "error", name: "SeatDoesNotExist", inputs: [] },
  { type: "error", name: "NotSeatHolder", inputs: [] },
  { type: "error", name: "NotPoolCreator", inputs: [] },
  { type: "error", name: "CannotBuyOwnSeat", inputs: [] },
  { type: "error", name: "PriceMustBePositive", inputs: [] },
  { type: "error", name: "InsufficientDeposit", inputs: [] },
  { type: "error", name: "InsufficientBalance", inputs: [] },
  { type: "error", name: "CooldownActive", inputs: [] },
  { type: "error", name: "PoolNotRegistered", inputs: [] },
  { type: "error", name: "PriceExceedsMax", inputs: [] },
  { type: "error", name: "TaxRateOutOfBounds", inputs: [] },
  { type: "error", name: "InsufficientPayment", inputs: [] },
  { type: "error", name: "ProtocolFeeOutOfBounds", inputs: [] },
  { type: "error", name: "ZeroAddress", inputs: [] },
  { type: "error", name: "InvalidInput", inputs: [] },
  { type: "error", name: "TransferFailed", inputs: [] },
  { type: "error", name: "TokenNotApprovedForDeposit", inputs: [] },
  { type: "error", name: "NotAllSeatsHeld", inputs: [] },
  { type: "error", name: "NotApprovedZap", inputs: [] },
] as const;

/**
 * Legacy manager ABI — identical interface to the main manager.
 * Each legacy manager handles exactly one pool.
 */
export const TAKEOVER_MANAGER_LEGACY_ABI = TAKEOVER_MANAGER_ABI;

/**
 * V2 UnifiedGridManager ABI.
 * Key difference from V1: all functions take uint256 gridId instead of bytes32 poolId,
 * and seats() returns a struct (address,uint96,uint96,uint32,uint32,uint32,uint32,bool).
 */
export const UGM_ABI = [
  {
    name: "addBatch",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [
      { name: "gridId", type: "uint256" },
      {
        name: "params",
        type: "tuple[]",
        components: [
          { name: "seatId", type: "uint256" },
          { name: "price", type: "uint256" },
          { name: "maxPrice", type: "uint256" },
          { name: "amount", type: "uint256" },
        ],
      },
    ],
    outputs: [],
  },
  {
    name: "setPrice",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [
      { name: "gridId", type: "uint256" },
      { name: "seatId", type: "uint256" },
      { name: "newPrice", type: "uint256" },
    ],
    outputs: [],
  },
  {
    name: "addDeposit",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [
      { name: "gridId", type: "uint256" },
      { name: "seatId", type: "uint256" },
      { name: "amount", type: "uint256" },
    ],
    outputs: [],
  },
  {
    name: "withdrawDeposit",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [
      { name: "gridId", type: "uint256" },
      { name: "seatId", type: "uint256" },
      { name: "amount", type: "uint256" },
    ],
    outputs: [],
  },
  {
    name: "abandonSeat",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [
      { name: "gridId", type: "uint256" },
      { name: "seatId", type: "uint256" },
    ],
    outputs: [],
  },
  {
    name: "pendingFees",
    type: "function",
    stateMutability: "view",
    inputs: [
      { name: "gridId", type: "uint256" },
      { name: "holder", type: "address" },
    ],
    outputs: [{ name: "", type: "uint256" }],
  },
  {
    name: "claimFees",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [{ name: "gridIds", type: "uint256[]" }],
    outputs: [{ name: "total", type: "uint256" }],
  },
  {
    name: "pokeTax",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [
      { name: "gridId", type: "uint256" },
      { name: "seatIds", type: "uint256[]" },
    ],
    outputs: [],
  },
  {
    name: "holderSeatCount",
    type: "function",
    stateMutability: "view",
    inputs: [
      { name: "gridId", type: "uint256" },
      { name: "holder", type: "address" },
    ],
    outputs: [{ name: "", type: "uint256" }],
  },
  {
    name: "seats",
    type: "function",
    stateMutability: "view",
    inputs: [
      { name: "gridId", type: "uint256" },
      { name: "seatId", type: "uint256" },
    ],
    outputs: [
      { name: "holder", type: "address" },
      { name: "price", type: "uint96" },
      { name: "deposit", type: "uint96" },
      { name: "lastTaxTime", type: "uint32" },
      { name: "lastPriceChangeTime", type: "uint32" },
      { name: "holderSince", type: "uint32" },
      { name: "forfeitedAt", type: "uint32" },
      { name: "seatEverSold", type: "bool" },
    ],
  },
  {
    name: "withdrawAsset",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [
      { name: "gridId", type: "uint256" },
      { name: "assetHash", type: "bytes32" },
    ],
    outputs: [],
  },
  {
    name: "creatorCloseGrid",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [{ name: "gridId", type: "uint256" }],
    outputs: [
      { name: "totalDeposit", type: "uint256" },
      { name: "totalFees", type: "uint256" },
    ],
  },
  {
    name: "abandonBatch",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [
      { name: "gridId", type: "uint256" },
      { name: "seatIds", type: "uint256[]" },
    ],
    outputs: [],
  },
  {
    name: "currentBalance",
    type: "function",
    stateMutability: "view",
    inputs: [
      { name: "gridId", type: "uint256" },
      { name: "seatId", type: "uint256" },
    ],
    outputs: [{ name: "", type: "int256" }],
  },
  {
    name: "cooldownRemaining",
    type: "function",
    stateMutability: "view",
    inputs: [
      { name: "gridId", type: "uint256" },
      { name: "seatId", type: "uint256" },
    ],
    outputs: [{ name: "", type: "uint256" }],
  },
  {
    name: "ANTI_SNIPE_WINDOW",
    type: "function",
    stateMutability: "view",
    inputs: [],
    outputs: [{ name: "", type: "uint256" }],
  },
  {
    name: "gridCreationPaused",
    type: "function",
    stateMutability: "view",
    inputs: [],
    outputs: [{ name: "", type: "bool" }],
  },
  {
    name: "activeTaxToken",
    type: "function",
    stateMutability: "view",
    inputs: [],
    outputs: [{ name: "", type: "address" }],
  },
  {
    name: "taxRevenue",
    type: "function",
    stateMutability: "view",
    inputs: [{ name: "gridId", type: "uint256" }],
    outputs: [{ name: "", type: "uint256" }],
  },
  {
    name: "withdrawTaxRevenue",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [{ name: "gridId", type: "uint256" }],
    outputs: [],
  },
  { type: "error", name: "GridDoesNotExist", inputs: [] },
  { type: "error", name: "SeatDoesNotExist", inputs: [] },
  { type: "error", name: "SeatAlreadyClaimed", inputs: [] },
  { type: "error", name: "SeatNotClaimed", inputs: [] },
  { type: "error", name: "NotSeatHolder", inputs: [] },
  { type: "error", name: "CannotBuyOwnSeat", inputs: [] },
  { type: "error", name: "PriceMustBePositive", inputs: [] },
  { type: "error", name: "PriceExceedsMax", inputs: [] },
  { type: "error", name: "InsufficientPayment", inputs: [] },
  { type: "error", name: "InsufficientDeposit", inputs: [] },
  { type: "error", name: "InsufficientBalance", inputs: [] },
  { type: "error", name: "CooldownActive", inputs: [] },
  { type: "error", name: "GridPaused", inputs: [] },
  { type: "error", name: "GridIsDeprecated", inputs: [] },
  { type: "error", name: "AntiSnipeActive", inputs: [] },
  {
    name: "gridConfig",
    type: "function",
    stateMutability: "view",
    inputs: [{ name: "gridId", type: "uint256" }],
    outputs: [
      { name: "creator", type: "address" },
      { name: "createdAt", type: "uint64" },
      { name: "totalSeats", type: "uint16" },
      { name: "taxRateBps", type: "uint16" },
      { name: "taxToken", type: "address" },
      { name: "yieldToken", type: "address" },
    ],
  },
  {
    // Auto-generated public getter for `mapping(uint => uint16) public seatsInitialized`.
    // Used to detect partially-initialized grids: if a launch flow's
    // batched `initializeSeats` calls fail mid-sequence the grid is
    // live on-chain but no seat can be acquired until the remaining
    // batches confirm. Surfaced via the per-grid snapshot endpoint
    // (`/api/grid/[ugm]/[gridId]`) so the board page can render a
    // "resume initialization" affordance for the creator.
    name: "seatsInitialized",
    type: "function",
    stateMutability: "view",
    inputs: [{ name: "", type: "uint256" }],
    outputs: [{ name: "", type: "uint16" }],
  },
  {
    name: "GridCreationIsPaused",
    type: "error",
    inputs: [],
  },
  {
    name: "OnlyOneTaxTokenAllowed",
    type: "error",
    inputs: [],
  },
  // ───────────────────────────────────────────────────────────────────
  // V22 additions (additive — never called against V21 deployments).
  // V22-only selectors revert on V21; gate via `isV22Grid(managerAddress)`
  // before invoking. See PLAN_UGM_V22_APP_INDEXER.md §2.
  // ───────────────────────────────────────────────────────────────────
  {
    // V22-only — Dutch auction reclaim price for a forfeited seat.
    // Returns 0 for live seats and seats whose decay window has elapsed.
    name: "dutchAuctionPrice",
    type: "function",
    stateMutability: "view",
    inputs: [
      { name: "gridId", type: "uint256" },
      { name: "seatId", type: "uint256" },
    ],
    outputs: [{ name: "", type: "uint256" }],
  },
  {
    // V22-only — exposes forfeitureDuration + lazy-seat counters.
    // V21 has no equivalent; use `gridConfig` for shared fields and
    // count `seatsSold` from event stream.
    name: "gridConfigV22",
    type: "function",
    stateMutability: "view",
    inputs: [{ name: "gridId", type: "uint256" }],
    outputs: [
      { name: "forfeitureDuration", type: "uint32" },
      { name: "seatsMaterialized", type: "uint16" },
      { name: "seatsSold", type: "uint16" },
      { name: "graduated", type: "bool" },
    ],
  },
  {
    // V21+V22 — already present on V21 (NotApprovedGovernanceModule
    // path), kept here for ABI completeness around the per-grid
    // governance lookup (§4.2).
    name: "gridGovernanceModule",
    type: "function",
    stateMutability: "view",
    inputs: [{ name: "gridId", type: "uint256" }],
    outputs: [{ name: "", type: "address" }],
  },
  {
    // V22-only — emitted alongside SeatBought when a forfeited seat is
    // reclaimed via Dutch auction. `clearingPrice` is the decayed
    // price routed to taxRevenue (creator).
    name: "SeatReclaimedDutch",
    type: "event",
    inputs: [
      { name: "gridId", type: "uint256", indexed: true },
      { name: "seatId", type: "uint256", indexed: true },
      { name: "buyer", type: "address", indexed: true },
      { name: "clearingPrice", type: "uint256", indexed: false },
    ],
    anonymous: false,
  },
  {
    // V21+V22 — emitted whenever a grid creator sets/changes the
    // governance module. Indexer §3.3.5 caches into Grid.governanceModule.
    name: "GridGovernanceModuleSet",
    type: "event",
    inputs: [
      { name: "gridId", type: "uint256", indexed: true },
      { name: "module", type: "address", indexed: true },
    ],
    anonymous: false,
  },
  {
    // V22-only — `forfeitureDuration` exceeds MAX_FORFEITURE_DURATION
    // (30 days) at createGrid time.
    name: "ForfeitureDurationOutOfBounds",
    type: "error",
    inputs: [],
  },
  {
    // V21+V22 — `setGridGovernanceModule` called with a non-allowlisted
    // module. Carried in V22 for parity.
    name: "NotApprovedGovernanceModule",
    type: "error",
    inputs: [],
  },
] as const;

/**
 * V22-shaped `createGrid` ABI. The selector differs from V21:
 *   V21: createGrid((uint16,uint16,address,address,uint128[]))
 *   V22: createGrid((uint16,uint16,uint32,address,address,uint128[]))
 * (extra `forfeitureDuration` field; `seatPrices` renamed to `initialPrices`)
 *
 * Use this export for V22 launches; the launch flow targets V22 only
 * (PLAN §4.6, open-question Q1 resolved: hardcode V22). V21 grids are
 * still readable via UGM_ABI but new V21 grids are not created here.
 */
export const UGM_V22_CREATE_GRID_ABI = [
  {
    name: "createGrid",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [
      {
        name: "params",
        type: "tuple",
        components: [
          { name: "taxRateBps", type: "uint16" },
          { name: "totalSeats", type: "uint16" },
          { name: "forfeitureDuration", type: "uint32" },
          { name: "taxToken", type: "address" },
          { name: "yieldToken", type: "address" },
          { name: "initialPrices", type: "uint128[]" },
        ],
      },
    ],
    outputs: [{ name: "gridId", type: "uint256" }],
  },
] as const;

/**
 * TakeoverLegacyFactory ABI — deploys + tracks 1:1 legacy managers.
 */
export const TAKEOVER_LEGACY_FACTORY_ABI = [
  {
    name: "managers",
    type: "function",
    stateMutability: "view",
    inputs: [
      { name: "_flaunch", type: "address" },
      { name: "_tokenId", type: "uint256" },
    ],
    outputs: [{ name: "", type: "address" }],
  },
  {
    name: "allManagerAddresses",
    type: "function",
    stateMutability: "view",
    inputs: [],
    outputs: [{ name: "", type: "address[]" }],
  },
  {
    name: "allManagersLength",
    type: "function",
    stateMutability: "view",
    inputs: [],
    outputs: [{ name: "", type: "uint256" }],
  },
] as const;

/**
 * TakeoverBuyback contract ABI
 * Derived from takeover-contracts/src/TakeoverBuyback.sol
 */
export const TAKEOVER_BUYBACK_ABI = [
  // =========================================================================
  // View Functions
  // =========================================================================
  {
    name: "owner",
    type: "function",
    stateMutability: "view",
    inputs: [],
    outputs: [{ name: "", type: "address" }],
  },
  {
    name: "takeoverToken",
    type: "function",
    stateMutability: "view",
    inputs: [],
    outputs: [{ name: "", type: "address" }],
  },
  {
    name: "tokenReceiver",
    type: "function",
    stateMutability: "view",
    inputs: [],
    outputs: [{ name: "", type: "address" }],
  },
  {
    name: "v3PoolFee",
    type: "function",
    stateMutability: "view",
    inputs: [],
    outputs: [{ name: "", type: "uint24" }],
  },
  {
    name: "minBuybackAmount",
    type: "function",
    stateMutability: "view",
    inputs: [],
    outputs: [{ name: "", type: "uint256" }],
  },
  // =========================================================================
  // Write Functions - Admin (onlyOwner)
  // =========================================================================
  {
    name: "setTakeoverToken",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [{ name: "_token", type: "address" }],
    outputs: [],
  },
  {
    name: "setTokenReceiver",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [{ name: "_receiver", type: "address" }],
    outputs: [],
  },
  {
    name: "setV3PoolFee",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [{ name: "_fee", type: "uint24" }],
    outputs: [],
  },
  {
    name: "setMinBuybackAmount",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [{ name: "_amount", type: "uint256" }],
    outputs: [],
  },
  {
    name: "transferOwnership",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [{ name: "newOwner", type: "address" }],
    outputs: [],
  },
  // =========================================================================
  // Write Functions - Buyback (onlyOwner)
  // =========================================================================
  {
    name: "buyback",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [{ name: "_usdcAmount", type: "uint256" }],
    outputs: [],
  },
  {
    name: "buyback",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [
      { name: "_usdcAmount", type: "uint256" },
      { name: "_minTakeoverOut", type: "uint256" },
    ],
    outputs: [],
  },
  // =========================================================================
  // Write Functions - Emergency (onlyOwner)
  // =========================================================================
  {
    name: "emergencyWithdraw",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [
      { name: "_token", type: "address" },
      { name: "_to", type: "address" },
      { name: "_amount", type: "uint256" },
    ],
    outputs: [],
  },
  {
    name: "emergencyWithdrawETH",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [{ name: "_to", type: "address" }],
    outputs: [],
  },
  // =========================================================================
  // Custom Errors
  // =========================================================================
  { type: "error", name: "ZeroAddress", inputs: [] },
  { type: "error", name: "TakeoverTokenNotSet", inputs: [] },
  { type: "error", name: "TokenReceiverNotSet", inputs: [] },
  { type: "error", name: "NothingToBuyback", inputs: [] },
  { type: "error", name: "InsufficientOutputAmount", inputs: [] },
  { type: "error", name: "BelowMinBuybackAmount", inputs: [] },
  { type: "error", name: "InvalidV3FeeTier", inputs: [] },
  { type: "error", name: "ETHTransferFailed", inputs: [] },
] as const;

export const ERC721_ABI = [
  {
    name: "approve",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [
      { name: "to", type: "address" },
      { name: "tokenId", type: "uint256" },
    ],
    outputs: [],
  },
  {
    name: "getApproved",
    type: "function",
    stateMutability: "view",
    inputs: [{ name: "tokenId", type: "uint256" }],
    outputs: [{ name: "", type: "address" }],
  },
  {
    name: "ownerOf",
    type: "function",
    stateMutability: "view",
    inputs: [{ name: "tokenId", type: "uint256" }],
    outputs: [{ name: "", type: "address" }],
  },
  {
    name: "tokenURI",
    type: "function",
    stateMutability: "view",
    inputs: [{ name: "tokenId", type: "uint256" }],
    outputs: [{ name: "", type: "string" }],
  },
  {
    name: "balanceOf",
    type: "function",
    stateMutability: "view",
    inputs: [{ name: "owner", type: "address" }],
    outputs: [{ name: "", type: "uint256" }],
  },
  {
    name: "tokenOfOwnerByIndex",
    type: "function",
    stateMutability: "view",
    inputs: [
      { name: "owner", type: "address" },
      { name: "index", type: "uint256" },
    ],
    outputs: [{ name: "", type: "uint256" }],
  },
] as const;

/**
 * Flaunch-specific ABI for reading coin metadata from the NFT contract.
 */
export const FLAUNCH_ABI = [
  {
    name: "tokenId",
    type: "function",
    stateMutability: "view",
    inputs: [{ name: "_memecoin", type: "address" }],
    outputs: [{ name: "", type: "uint256" }],
  },
  {
    name: "memecoin",
    type: "function",
    stateMutability: "view",
    inputs: [{ name: "_tokenId", type: "uint256" }],
    outputs: [{ name: "", type: "address" }],
  },
  {
    name: "positionManager",
    type: "function",
    stateMutability: "view",
    inputs: [],
    outputs: [{ name: "", type: "address" }],
  },
] as const;

export const MEMECOIN_ABI = [
  {
    name: "name",
    type: "function",
    stateMutability: "view",
    inputs: [],
    outputs: [{ name: "", type: "string" }],
  },
  {
    name: "symbol",
    type: "function",
    stateMutability: "view",
    inputs: [],
    outputs: [{ name: "", type: "string" }],
  },
] as const;

export const TAKEOVER_BUYBACK_KEEPER_ABI = [
  {
    name: "keeperBuybackAmount",
    type: "function",
    stateMutability: "view",
    inputs: [],
    outputs: [{ name: "", type: "uint256" }],
  },
  {
    name: "keeperRewardBps",
    type: "function",
    stateMutability: "view",
    inputs: [],
    outputs: [{ name: "", type: "uint256" }],
  },
  {
    name: "takeoverPoolId",
    type: "function",
    stateMutability: "view",
    inputs: [],
    outputs: [{ name: "", type: "bytes32" }],
  },
  {
    name: "keeperBuyback",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [],
    outputs: [],
  },
] as const;

export const ERC20_ABI = [
  {
    name: "approve",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [
      { name: "spender", type: "address" },
      { name: "amount", type: "uint256" },
    ],
    outputs: [{ name: "", type: "bool" }],
  },
  {
    name: "allowance",
    type: "function",
    stateMutability: "view",
    inputs: [
      { name: "owner", type: "address" },
      { name: "spender", type: "address" },
    ],
    outputs: [{ name: "", type: "uint256" }],
  },
  {
    name: "balanceOf",
    type: "function",
    stateMutability: "view",
    inputs: [{ name: "account", type: "address" }],
    outputs: [{ name: "", type: "uint256" }],
  },
  {
    name: "transfer",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [
      { name: "to", type: "address" },
      { name: "amount", type: "uint256" },
    ],
    outputs: [{ name: "", type: "bool" }],
  },
] as const;

/**
 * FlaunchYieldAdapter ABI — pendingYield view for uncollected Flaunch fees.
 */
export const FLAUNCH_YIELD_ADAPTER_ABI = [
  {
    name: "pendingYield",
    type: "function",
    stateMutability: "view",
    inputs: [{ name: "assetHash", type: "bytes32" }],
    outputs: [{ name: "", type: "uint256" }],
  },
  {
    name: "deposit",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [
      { name: "flaunch", type: "address" },
      { name: "tokenId", type: "uint256" },
      { name: "gridId", type: "uint256" },
    ],
    outputs: [],
  },
  // The adapter accumulates fees in flETH and the yield-cap path
  // (`/api/grid/[ugm]/[gridId]`) needs the canonical token address when
  // the indexer's `Grid.yieldToken` is still 0x0 (post-deploy / pre-flush).
  // Exposed off the adapter rather than hardcoded so a redeployed
  // adapter pointed at a different wrapper still resolves correctly.
  {
    name: "flETH",
    type: "function",
    stateMutability: "view",
    inputs: [],
    outputs: [{ name: "", type: "address" }],
  },
] as const;

/**
 * V3YieldAdapter ABI — pendingYield view for uncollected LP fees per asset.
 */
export const V3_YIELD_ADAPTER_ABI = [
  {
    name: "pendingYield",
    type: "function",
    stateMutability: "view",
    inputs: [{ name: "assetHash", type: "bytes32" }],
    outputs: [
      { name: "token0Owed", type: "uint256" },
      { name: "token1Owed", type: "uint256" },
    ],
  },
  {
    name: "deposit",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [
      { name: "positionManager", type: "address" },
      { name: "tokenId", type: "uint256" },
      { name: "gridId", type: "uint256" },
    ],
    outputs: [],
  },
] as const;

/**
 * V4YieldAdapter ABI — pendingYield view for uncollected V4 LP fees per asset.
 */
export const V4_YIELD_ADAPTER_ABI = [
  {
    name: "pendingYield",
    type: "function",
    stateMutability: "view",
    inputs: [{ name: "assetHash", type: "bytes32" }],
    outputs: [{ name: "", type: "uint256" }],
  },
  {
    name: "deposit",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [
      { name: "tokenId", type: "uint256" },
      { name: "gridId", type: "uint256" },
    ],
    outputs: [],
  },
] as const;

/**
 * WhitelistGovernanceModule ABI — reservation view functions for Phase 1 hvTAKEOVER.
 */
export const WHITELIST_GOVERNANCE_ABI = [
  {
    name: "claimDeadline",
    type: "function",
    stateMutability: "view",
    inputs: [],
    outputs: [{ name: "", type: "uint256" }],
  },
  {
    name: "getUnclaimedReservations",
    type: "function",
    stateMutability: "view",
    inputs: [
      { name: "ugm", type: "address" },
      { name: "gridId", type: "uint256" },
      { name: "totalSeats", type: "uint256" },
    ],
    outputs: [
      { name: "seatIds", type: "uint256[]" },
      { name: "claimants", type: "address[]" },
    ],
  },
] as const;

/**
 * Shared adapter withdraw ABI — all yield adapters (V3, V4, Flaunch) share this signature.
 */
export const YIELD_ADAPTER_WITHDRAW_ABI = [
  {
    name: "withdraw",
    type: "function",
    stateMutability: "nonpayable",
    inputs: [
      { name: "assetHash", type: "bytes32" },
      { name: "recipient", type: "address" },
    ],
    outputs: [],
  },
] as const;
